import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import type {
  PcmpCmpReport,
  PcmpCmpReportExtraColumn,
  PcmpCmpReportRow,
  PcmpCmpReportSection,
  PcmpCmpTotals,
} from '../../types/pcmpReport.types';

/** A batch's report: the shared CMP report and the batch it is of. */
export type PcmpBatchReportData = PcmpCmpReport & { batch: { batchNo: number } };

const money = (value: number) =>
  value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const percent = (value: number | null) => (value === null ? EMPTY_VALUE : `${value.toFixed(2)}%`);
const text = (value: string | null) => (value && value.trim() !== '' ? value : EMPTY_VALUE);

const cellClass = 'border border-neutral-400 px-1 py-0.5 align-top';
const numClass = 'text-right tabular-nums whitespace-nowrap';

function Th({
  children,
  className,
  ...rest
}: {
  children: ReactNode;
  className?: string;
  colSpan?: number;
  rowSpan?: number;
}) {
  return (
    <th {...rest} className={cn(cellClass, 'bg-neutral-100 text-center font-semibold', className)}>
      {children}
    </th>
  );
}

function Td({
  children,
  className,
  ...rest
}: {
  children: ReactNode;
  className?: string;
  colSpan?: number;
}) {
  return (
    <td {...rest} className={cn(cellClass, className)}>
      {children}
    </td>
  );
}

function paymentText({ payment }: PcmpCmpReportRow): string {
  const account = [payment.bankName, payment.branchName, payment.accountNo, payment.routingNo]
    .filter((part) => part && part.trim() !== '')
    .join(', ');
  return [payment.type, account || payment.mobileNo, payment.holderName, payment.onBehalf]
    .filter((part) => part && part.trim() !== '')
    .join(' · ');
}

function SummaryCells({
  qty,
  current,
  overall,
  proposed,
}: {
  qty: number;
  current: PcmpCmpTotals;
  overall: PcmpCmpTotals;
  proposed: number;
}) {
  return (
    <>
      <Td className={numClass}>{formatNumber(overall.instituteCount)}</Td>
      <Td className={numClass}>{money(overall.salesTarget)}</Td>
      <Td className={numClass}>{money(overall.cmpAmount)}</Td>
      <Td className={numClass}>{percent(overall.cmpPercent)}</Td>
      <Td className={numClass}>{formatNumber(qty)}</Td>
      <Td className={numClass}>{formatNumber(current.instituteCount)}</Td>
      <Td className={numClass}>{money(current.salesTarget)}</Td>
      <Td className={numClass}>{money(current.cmpAmount)}</Td>
      <Td className={numClass}>{percent(current.cmpPercent)}</Td>
      <Td className={numClass}>{money(proposed)}</Td>
    </>
  );
}

function DivisionSummary({ report }: { report: PcmpCmpReport }) {
  return (
    <table className="w-full border-collapse">
      <thead>
        <tr>
          <Th rowSpan={2}>Division</Th>
          <Th colSpan={4}>Overall</Th>
          <Th colSpan={6}>Current Batch</Th>
        </tr>
        <tr>
          <Th>Total Institutes</Th>
          <Th>Sales Target (TK)</Th>
          <Th>Total CMP (TK)</Th>
          <Th>Total %</Th>
          <Th>Proposal Qty</Th>
          <Th>Total Institutes</Th>
          <Th>Sales Target (TK)</Th>
          <Th>Total CMP Amount (Agreed) (TK)</Th>
          <Th>%</Th>
          <Th>Proposed Amount (TK)</Th>
        </tr>
      </thead>
      <tbody>
        {report.sections.map((section) => (
          <tr key={section.division}>
            <Td>{section.division}</Td>
            <SummaryCells
              qty={section.proposalQty}
              current={section.current}
              overall={section.overall}
              proposed={section.proposedAmount}
            />
          </tr>
        ))}
        <tr className="font-semibold">
          <Td>Total</Td>
          <SummaryCells
            qty={report.proposalQty}
            current={report.current}
            overall={report.overall}
            proposed={report.proposedAmount}
          />
        </tr>
      </tbody>
    </table>
  );
}

function extraText(row: PcmpCmpReportRow, column: PcmpCmpReportExtraColumn): string {
  if (column === 'STATUS') return text(row.status ?? null);
  return row.origBatchNo == null ? EMPTY_VALUE : `#${row.origBatchNo}`;
}

function DetailRow({
  row,
  extraColumns,
}: {
  row: PcmpCmpReportRow;
  extraColumns: PcmpCmpReportExtraColumn[];
}) {
  return (
    <tr className="break-inside-avoid">
      <Td className={numClass}>{row.sl}</Td>
      <Td>{row.division}</Td>
      <Td>{text(row.region)}</Td>
      <Td className="whitespace-nowrap">
        {/* Legacy LDS-772: the proposal opens beside the report, which stays put. */}
        <Link
          to="/pcmp/proposals/$proposalId"
          params={{ proposalId: row.proposalId }}
          target="_blank"
          className="text-primary hover:underline print:text-inherit"
        >
          {row.refNo}
        </Link>
      </Td>
      <Td>{row.projectName}</Td>
      <Td>{row.moInfo}</Td>
      <Td className="whitespace-nowrap">{text(row.moHrEmpId)}</Td>
      <Td className="whitespace-nowrap">{text(row.moMobile)}</Td>
      <Td>{text(row.instituteThana)}</Td>
      <Td>{text(row.instituteDistrict)}</Td>
      <Td className={numClass}>{row.instituteId ?? EMPTY_VALUE}</Td>
      <Td>{text(row.instituteName)}</Td>
      <Td>{row.teacherName}</Td>
      <Td className={numClass}>{formatNumber(row.studentQty)}</Td>
      <Td className={numClass}>{money(row.salesTarget)}</Td>
      <Td className={numClass}>{percent(row.grammarPercent)}</Td>
      <Td className={numClass}>{percent(row.practicePercent)}</Td>
      <Td className={numClass}>{percent(row.cmpPercent)}</Td>
      <Td className={numClass}>{money(row.perStudentCost)}</Td>
      <Td className={numClass}>{money(row.cmpAmount)}</Td>
      <Td className={numClass}>{money(row.proposedAmount)}</Td>
      {extraColumns.map((column) => (
        <Td key={column} className="whitespace-nowrap">
          {extraText(row, column)}
        </Td>
      ))}
      <Td>{text(paymentText(row))}</Td>
    </tr>
  );
}

function SectionTotal({
  label,
  section,
  extraCount,
}: {
  label: string;
  section: Pick<PcmpCmpReportSection, 'proposalQty' | 'proposedAmount' | 'current'>;
  extraCount: number;
}) {
  return (
    <tr className="break-inside-avoid bg-neutral-50 font-semibold">
      <Td colSpan={13}>
        {label} · {formatNumber(section.proposalQty)} proposals
      </Td>
      <Td className={numClass}>{EMPTY_VALUE}</Td>
      <Td className={numClass}>{money(section.current.salesTarget)}</Td>
      <Td colSpan={2}>{''}</Td>
      <Td className={numClass}>{percent(section.current.cmpPercent)}</Td>
      <Td>{''}</Td>
      <Td className={numClass}>{money(section.current.cmpAmount)}</Td>
      <Td className={numClass}>{money(section.proposedAmount)}</Td>
      <Td colSpan={extraCount + 1}>{''}</Td>
    </tr>
  );
}

/**
 * The CMP disbursement report of a batch (MIS routing or Accounts), laid out like the batch
 * Excel: header, division summary, then the rows by division. Long batches
 * run over several landscape pages; the detail header repeats on each.
 */
export function PcmpBatchReport({ report }: { report: PcmpBatchReportData }) {
  return (
    <div className="flex flex-col gap-3 text-[8px] leading-tight text-black">
      <header className="flex flex-col items-center gap-0.5 text-center">
        <span className="text-[13px] font-bold">{report.company.name}</span>
        <span className="text-[9px]">{report.company.address}</span>
        <span className="mt-1 text-[12px] font-semibold">{report.title}</span>
      </header>

      <div className="flex flex-wrap justify-between gap-2 border-y border-neutral-400 py-1 text-[9px] font-medium">
        <span>Batch No: #{report.batch.batchNo}</span>
        <span>Total Proposal Qty: {formatNumber(report.proposalQty)}</span>
        <span>Total Amount (TK): {money(report.proposedAmount)}</span>
        <span>Date: {formatDisplayDate(report.reportDate)}</span>
      </div>

      <DivisionSummary report={report} />

      <table className="w-full border-collapse">
        <thead className="table-header-group">
          <tr>
            {report.columns.map((column) => (
              <Th key={column}>{column}</Th>
            ))}
            <Th>Payment</Th>
          </tr>
        </thead>
        <tbody>
          {report.sections.map((section) => [
            ...section.rows.map((row) => (
              <DetailRow key={row.proposalId} row={row} extraColumns={report.extraColumns} />
            )),
            <SectionTotal
              key={`${section.division}-total`}
              label={`${section.division} total`}
              section={section}
              extraCount={report.extraColumns.length}
            />,
          ])}
          <SectionTotal
            label="Grand total"
            section={report}
            extraCount={report.extraColumns.length}
          />
        </tbody>
      </table>
    </div>
  );
}
